import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import {
  Popover as HeadlessUIPopover,
  PopoverBackdrop as HeadlessUIPopoverBackdrop,
  PopoverButton as HeadlessUIPopoverButton,
  PopoverPanel as HeadlessUIPopoverPanel,
  Portal,
} from "@headlessui/react";
import { cx } from "@jugl-web/utils";
import {
  CSSProperties,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  useMemo,
} from "react";
import { Checkbox, PlainButton, Switch } from "../../primitives";
import { Radio } from "../../forms/Radio";
import { MenuItem } from "../Menu";
import { ReactComponent as MoreArrow } from "./assets/more-arrow.svg";

export type MenuItemSubmenu<TValue = unknown> = {
  placement: Placement;
  selectedId?: string;
  className?: string;
  sections: PopoverMenuItem<TValue>[][];
  onSelect?: (
    item: PopoverMenuItem<TValue>,
    event: MouseEvent<HTMLButtonElement>,
    close: () => void
  ) => void;
};

export type PopoverMenuItem<TValue = unknown> = MenuItem<TValue> & {
  submenu?: MenuItemSubmenu<TValue>;
};

export type PopoverMenuSections<TValue = unknown> = PopoverMenuItem<TValue>[][];

interface RenderTriggerProps {
  Trigger: typeof HeadlessUIPopoverButton;
  triggerRef: Ref<HTMLButtonElement>;
  isOpen: boolean;
  onClose: () => void;
}

export type PopoverMenuProps<TValue = unknown> = {
  placement: Placement;
  sections: PopoverMenuSections<TValue>;
  selectedId?: string;
  adjustToTriggerWidth?: boolean;
  className?: string;
  autoPosition?: boolean;
  hasBackdrop?: boolean;
  renderTrigger: (props: RenderTriggerProps) => ReactElement;
  onSelect?: (
    item: PopoverMenuItem<TValue>,
    event: MouseEvent<HTMLButtonElement>,
    close: () => void
  ) => void;
};

const renderMenuItemToggle = (
  type: PopoverMenuItem["toggle"],
  isSelected: boolean
): ReactNode => {
  if (type === "checkbox") {
    return (
      <Checkbox
        readOnly
        isChecked={isSelected}
        key={String(isSelected)}
        className="m-0"
      />
    );
  }
  if (type === "radio") {
    return (
      <Radio
        readOnly
        isChecked={isSelected}
        key={String(isSelected)}
        className="m-0"
      />
    );
  }
  if (type === "switch") {
    return (
      <Switch
        as="div"
        isChecked={isSelected}
        key={String(isSelected)}
        className="m-0"
      />
    );
  }
  return null;
};

interface SubmenuPopoverItemProps<TValue> {
  item: PopoverMenuItem<TValue> & { submenu: MenuItemSubmenu<TValue> };
  parentClose: () => void;
}

/**
 * A floating dropdown menu that supports nested submenus via HeadlessUI Popover.
 *
 * Unlike Menu (which uses HeadlessUI's Menu primitive and cannot nest), PopoverMenu
 * uses the Popover primitive so a sub-panel can open without collapsing the parent.
 */
export function PopoverMenu<TValue>({
  placement,
  sections,
  selectedId,
  adjustToTriggerWidth,
  className,
  autoPosition,
  hasBackdrop,
  renderTrigger,
  onSelect,
}: PopoverMenuProps<TValue>): ReactElement {
  const { refs, floatingStyles, elements } = useFloating<HTMLButtonElement>({
    placement,
    middleware: [flip(), shift(), offset({ mainAxis: 4 })],
    whileElementsMounted: autoPosition ? autoUpdate : undefined,
  });

  const panelWidth = useMemo<CSSProperties["width"]>(() => {
    if (!adjustToTriggerWidth || !elements.reference) {
      return undefined;
    }
    return elements.reference.getBoundingClientRect().width;
  }, [adjustToTriggerWidth, elements.reference]);

  return (
    <HeadlessUIPopover>
      {({ open, close }) => (
        <>
          {renderTrigger({
            Trigger: HeadlessUIPopoverButton,
            triggerRef: refs.setReference,
            isOpen: open,
            onClose: close,
          })}

          {hasBackdrop && open && (
            <HeadlessUIPopoverBackdrop className="fixed inset-0 z-9999" />
          )}

          <Portal>
            <HeadlessUIPopoverPanel
              as="div"
              ref={refs.setFloating}
              className={cx(
                "z-10000 max-h-[300px] min-w-[200px] overflow-y-auto rounded-xl bg-white outline-none",
                "divide-x-0 divide-y divide-solid divide-grey-200",
                className
              )}
              style={{
                boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.12)",
                width: panelWidth,
                ...floatingStyles,
              }}
            >
              {sections.map((section, sectionIdx) => {
                const hasSectionAnyItem = section.some(
                  (item) => !item.isHidden
                );

                if (!hasSectionAnyItem) {
                  return null;
                }

                return (
                  <div key={+sectionIdx}>
                    {section.map((item) => {
                      if (item.isHidden) {
                        return null;
                      }

                      /* Items with a submenu delegate to a nested Popover */
                      if (item.submenu) {
                        return (
                          <SubmenuPopoverItem
                            key={item.id}
                            item={
                              item as PopoverMenuItem<TValue> & {
                                submenu: MenuItemSubmenu<TValue>;
                              }
                            }
                            parentClose={close}
                          />
                        );
                      }

                      const isSelected =
                        (selectedId !== undefined &&
                          item.id !== undefined &&
                          selectedId === item.id) ||
                        !!item.isSelected;

                      return (
                        <div
                          key={item.id}
                          className={cx(
                            "flex items-center transition hover:bg-grey-100",
                            {
                              "bg-grey-200 font-semibold":
                                isSelected && !item.toggle,
                              "cursor-not-allowed opacity-25": item.isDisabled,
                            },
                            item.className
                          )}
                        >
                          <PlainButton
                            onClick={(event) => {
                              event.stopPropagation();
                              if (item.toggle) {
                                event.preventDefault();
                              }
                              onSelect?.(item, event, close);
                              item.onSelect?.(event, close);
                              /* Always close after selection so callers
                                 need not call close() explicitly */
                              close();
                            }}
                            className={cx(
                              "flex h-full w-full items-center gap-2.5 px-4 py-3",
                              item.triggerClassName,
                              {
                                "pointer-events-none":
                                  item.isDisabled || item.isReadonly,
                              }
                            )}
                          >
                            {item.icon && (
                              <span className="shrink-0">{item.icon}</span>
                            )}
                            {item.togglePlacement === "start" &&
                              item.toggle &&
                              renderMenuItemToggle(item.toggle, isSelected)}
                            <span
                              className={cx(
                                "grow text-left font-secondary text-sm leading-[16px] text-dark",
                                item.labelClassName
                              )}
                            >
                              {item.label}
                            </span>
                            {item.togglePlacement !== "start" &&
                              item.toggle &&
                              renderMenuItemToggle(item.toggle, isSelected)}
                            {item.endSlot}
                          </PlainButton>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </HeadlessUIPopoverPanel>
          </Portal>
        </>
      )}
    </HeadlessUIPopover>
  );
}

/**
 * Renders a menu-item-style button that opens a nested HeadlessUI Popover
 * panel when clicked. Selecting any sub-item closes both panels via combinedClose.
 */
const SubmenuPopoverItem = <TValue,>({
  item,
  parentClose,
}: SubmenuPopoverItemProps<TValue>) => {
  const { refs, floatingStyles } = useFloating<HTMLButtonElement>({
    placement: item.submenu.placement,
    middleware: [flip(), shift(), offset({ mainAxis: 4 })],
  });

  return (
    <HeadlessUIPopover>
      {({ open, close: closeSubmenu }) => {
        /** Closes both this nested panel and the outer panel together. */
        const combinedClose = () => {
          closeSubmenu();
          parentClose();
        };

        return (
          <>
            {/* Submenu trigger row — styled identically to regular items */}
            <div
              className={cx("flex items-center transition hover:bg-grey-100", {
                "bg-grey-100": open,
              })}
            >
              <HeadlessUIPopoverButton
                as={PlainButton}
                ref={refs.setReference}
                className="flex h-full w-full items-center gap-2.5 px-4 py-3"
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span
                  className={cx(
                    "grow text-left font-secondary text-sm leading-[16px] text-dark",
                    item.labelClassName
                  )}
                >
                  {item.label}
                </span>
                <MoreArrow />
              </HeadlessUIPopoverButton>
            </div>

            {/* Nested panel rendered in a Portal so it escapes any overflow clip */}
            <Portal>
              <HeadlessUIPopoverPanel
                as="div"
                ref={refs.setFloating}
                className={cx(
                  "z-10000 max-h-[300px] min-w-[200px] overflow-y-auto rounded-xl bg-white outline-none",
                  "divide-x-0 divide-y divide-solid divide-grey-200",
                  item.submenu.className
                )}
                style={{
                  boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.12)",
                  ...floatingStyles,
                }}
              >
                {item.submenu.sections.map((subSection, subIdx) => {
                  const hasSectionAnyItem = subSection.some(
                    (subItem) => !subItem.isHidden
                  );

                  if (!hasSectionAnyItem) {
                    return null;
                  }

                  return (
                    <div key={+subIdx}>
                      {subSection.map((subItem) => {
                        if (subItem.isHidden) {
                          return null;
                        }

                        const isSubSelected =
                          (item.submenu.selectedId !== undefined &&
                            subItem.id !== undefined &&
                            item.submenu.selectedId === subItem.id) ||
                          !!subItem.isSelected;

                        return (
                          <div
                            key={subItem.id}
                            className={cx(
                              "flex items-center transition hover:bg-grey-100",
                              {
                                "bg-grey-200 font-semibold":
                                  isSubSelected && !subItem.toggle,
                                "cursor-not-allowed opacity-25":
                                  subItem.isDisabled,
                              },
                              subItem.className
                            )}
                          >
                            <PlainButton
                              onClick={(event) => {
                                event.stopPropagation();
                                if (subItem.toggle) {
                                  event.preventDefault();
                                }
                                item.submenu.onSelect?.(
                                  subItem,
                                  event,
                                  combinedClose
                                );
                                subItem.onSelect?.(event, combinedClose);
                              }}
                              className={cx(
                                "flex h-full w-full items-center gap-2.5 px-4 py-3",
                                subItem.triggerClassName,
                                {
                                  "pointer-events-none":
                                    subItem.isDisabled || subItem.isReadonly,
                                }
                              )}
                            >
                              {subItem.icon && (
                                <span className="shrink-0">{subItem.icon}</span>
                              )}
                              {subItem.togglePlacement === "start" &&
                                subItem.toggle &&
                                renderMenuItemToggle(
                                  subItem.toggle,
                                  isSubSelected
                                )}
                              <span
                                className={cx(
                                  "grow text-left font-secondary text-sm leading-[16px] text-dark",
                                  subItem.labelClassName
                                )}
                              >
                                {subItem.label}
                              </span>
                              {subItem.togglePlacement !== "start" &&
                                subItem.toggle &&
                                renderMenuItemToggle(
                                  subItem.toggle,
                                  isSubSelected
                                )}
                              {subItem.endSlot}
                            </PlainButton>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </HeadlessUIPopoverPanel>
            </Portal>
          </>
        );
      }}
    </HeadlessUIPopover>
  );
};
